import React, { useState, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { updateEntity as updateQuotation } from 'app/entities/quotation/quotation.reducer';
import { Row, Col, Button } from 'reactstrap';
import { createTheme, responsiveFontSizes, ThemeProvider, Theme } from '@mui/material/styles';
import { Typography, Grid, TextField, Dialog, DialogTitle, DialogContent, DialogActions, FormHelperText } from '@mui/material';
import { Translate, translate } from 'react-jhipster';
import { IQuotation } from 'app/shared/model/quotation.model';
import TransactionRefNoDisplayButton from './transaction-reference-display-button'; // Adjust path

// Dialog Component
interface TransactionRefNoDialogProps {
  open: boolean;
  transactionRefNo: string;
  onClose: () => void;
  onConfirm: () => void;
  onChange: (value: string) => void;
}

const TransactionRefNoDialog: React.FC<TransactionRefNoDialogProps> = ({ open, transactionRefNo, onClose, onConfirm, onChange }) => {
  const isValid = transactionRefNo.trim() !== '';
  const errorMessage = !isValid ? translate('comexApp.quotation.transactionRefNoRequired', 'Transaction Reference Number is required') : '';

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <Translate contentKey="comexApp.quotation.inputTransactionRefNo">Input Transaction Reference Number</Translate>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label={translate('comexApp.quotation.transactionRefNo', 'Transaction Reference Number')}
          value={transactionRefNo}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
          margin="normal"
          variant="outlined"
          error={!isValid}
          helperText={errorMessage}
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          <Translate contentKey="entity.action.cancel">Cancel</Translate>
        </Button>
        <Button onClick={onConfirm} color="primary" disabled={!isValid}>
          <Translate contentKey="comexApp.quotation.confirmButton">Confirm</Translate>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Section Component
export const TransactionRefNoSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const quotationEntity = useAppSelector(state => state.quotation.entity);

  const [transactionRefNo, setTransactionRefNo] = useState<string>(quotationEntity?.transactionRefNo || '');
  const [openTransactionModal, setOpenTransactionModal] = useState<boolean>(false);

  const responsiveFont = useMemo(() => {
    let theme = createTheme();
    theme = responsiveFontSizes(theme);
    return theme;
  }, []);

  const handleTransactionRefNoChange = (value: string) => {
    setTransactionRefNo(value);
  };

  const handleTransactionModal = (buttonType: 'EDIT' | 'CLOSE' | 'ADDING-LATE') => {
    setOpenTransactionModal(buttonType !== 'CLOSE');
    if (buttonType === 'EDIT' || buttonType === 'ADDING-LATE') {
      setTransactionRefNo(quotationEntity?.transactionRefNo || '');
    }
  };

  const handleTransactionOption = () => {
    if (transactionRefNo !== null && transactionRefNo.trim()) {
      const quotationToSave: Partial<IQuotation> = {
        ...quotationEntity,
        transactionRefNo,
      };
      dispatch(updateQuotation(quotationToSave));
    }
  };

  const handleTransactionConfirm = () => {
    if (!transactionRefNo.trim()) {
      return;
    }
    setOpenTransactionModal(false);
    handleTransactionOption();
  };

  return (
    <div>
      {quotationEntity?.commodity?.ownership === 'Local' && (
        <Row className="justify-content-center" style={{ marginTop: '12px' }}>
          <Col md="8" lg="6" className="round-border mt-1" style={{ padding: '28px 42px' }}>
            <ThemeProvider theme={responsiveFont}>
              <Typography variant="body2" component="div">
                <strong>{translate('comexApp.quotation.transactionInformation')}</strong>
              </Typography>
            </ThemeProvider>

            <Grid style={{ marginTop: '20px' }} className="row">
              <Grid item xs={12} sm={6} md={4} sx={{ fontSize: '14px' }}>
                <p>
                  <Translate contentKey="comexApp.quotation.transactionRefNo">TRANSACTION REFERENCE NUMBER</Translate>
                </p>
              </Grid>
              <Grid item xs={12} sm={6} md={8}>
                <div>
                  <TransactionRefNoDisplayButton
                    transactionRefNo={transactionRefNo}
                    quotationEntity={quotationEntity}
                    handleTransactionModal={handleTransactionModal}
                  />
                </div>
              </Grid>
            </Grid>
          </Col>
        </Row>
      )}
      <TransactionRefNoDialog
        open={openTransactionModal}
        transactionRefNo={transactionRefNo}
        onClose={() => handleTransactionModal('CLOSE')}
        onConfirm={handleTransactionConfirm}
        onChange={handleTransactionRefNoChange}
      />
    </div>
  );
};
